import React from 'react';

interface AIUseCase {
  title: string;
  category: string;
  description: string;
  example: string;
  impact: string;
  icon: string;
}

const AI_USE_CASES: AIUseCase[] = [
  {
    title: 'Automated Invoice & Document Extraction',
    category: 'Smart OCR & Data Ingestion',
    description: 'Eliminate manual data entry by extracting structured JSON data directly from scanned PDFs, invoices, and paper contracts with zero human error.',
    example: 'Raw PDF Invoice ➔ Claude AI / Vision API ➔ Structured Database Entry + Ledger Sync',
    impact: '85% reduction in invoice processing time',
    icon: '📄',
  },
  {
    title: 'Predictive Inventory & Shift Staffing',
    category: 'Machine Learning Forecasting',
    description: 'Analyze historical sales, weather data, and seasonality patterns to predict exact ingredient usage and optimal shift staffing hours.',
    example: 'Historical POS Data + ML Model ➔ Automated Weekly Prep & Shift Recommendations',
    impact: 'Up to 24% reduction in food & ingredient waste',
    icon: '📈',
  },
  {
    title: 'Automated Re-Engagement AI Bots',
    category: 'Customer Retention & CRM',
    description: 'Trigger intelligent SMS and email follow-ups based on guest visit frequency, average spend, and dining preferences.',
    example: 'Customer inactive for 21 days ➔ Personalized AI Offer SMS ➔ 28% Re-engagement Rate',
    impact: '3.2x increase in customer repeat visits',
    icon: '💬',
  },
  {
    title: 'Real-Time Medical Terminology Assistant',
    category: 'WebRTC & Healthcare AI',
    description: 'Provide live, context-aware specialized terminology and glossaries to interpreters during active WebRTC video sessions.',
    example: 'Active Medical Call ➔ Live Audio Analysis ➔ Instant Terminology Popup for Interpreter',
    impact: 'Enhanced accuracy & faster call resolution',
    icon: '🏥',
  },
];

const AICapabilities: React.FC = () => {
  return (
    <section id="ai-solutions" className="py-24 bg-slate-950 text-white relative">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <p className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-3">
            Applied Artificial Intelligence
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
            AI That Solves Real Problems — <span className="text-blue-400">Not Vague Buzzwords</span>
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed">
            We build specialized, production-ready AI workflows integrated directly into your existing business software.
          </p>
        </div>

        {/* AI Use Cases Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {AI_USE_CASES.map((uc) => (
            <div
              key={uc.title}
              className="bg-slate-900/90 border border-slate-800 rounded-3xl p-8 hover:border-blue-500/50 transition-all duration-300 group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-6">
                  <span className="text-3xl p-3 bg-slate-800 rounded-2xl border border-slate-700">{uc.icon}</span>
                  <span className="px-3 py-1 bg-blue-500/10 text-blue-400 text-xs font-semibold rounded-full border border-blue-500/20">
                    {uc.category}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">{uc.title}</h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">{uc.description}</p>
              </div>

              <div className="space-y-4 pt-4 border-t border-slate-800">
                <div className="bg-slate-950 p-4 rounded-xl border border-slate-800/80 font-mono text-xs text-slate-300">
                  <span className="text-blue-400 font-semibold block mb-1">⚡ Real-world Workflow:</span>
                  {uc.example}
                </div>
                <div className="flex items-center justify-between text-xs font-semibold text-emerald-400 bg-emerald-950/40 px-4 py-2.5 rounded-xl border border-emerald-900/50">
                  <span>Proven Impact:</span>
                  <span className="font-mono text-sm">{uc.impact}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AICapabilities;
